import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import MoveIcon from '@cartwhl/ui/icons/MoveIcon';
import RotateIcon from '@cartwhl/ui/icons/RotateIcon';

import {
  hasNonIdentityScale,
  readBoneRestRotation,
  readBoneRestScale,
  readBoneRestTranslation,
  type AxisTriplet,
  type RestRotateMode,
  type RestTranslateMode,
} from '../../../lib/skeleton/boneTransforms';
import type { Bone } from '../../../lib/skeleton/core';
import { PANEL_WRITING_CLASSNAME } from '../../../presentations/panelWritingStyle';
import AxisTripletFields from './AxisTripletFields';
import Section from './Section';
import TransformAxisValuesRow from './TransformAxisValuesRow';

interface RestAxisValuesRowProps {
  label: string;
  labelIcon: ReactNode;
  compact: boolean;
  values: AxisTriplet;
  modeLabel: string;
  modeShortLabel: string;
  onToggleMode: () => void;
}

interface RestTransformViewerProps {
  bone: Bone;
  compactLayout?: boolean;
}

const COMPACT_LAYOUT_THRESHOLD_PX = 430;

const REST_TRANSLATE_MODE_LABEL: Record<RestTranslateMode, string> = {
  local: 'Local',
  world: 'World',
};
const REST_TRANSLATE_MODE_SHORT_LABEL: Record<RestTranslateMode, string> = {
  local: 'L',
  world: 'W',
};

const REST_ROTATE_MODE_LABEL: Record<RestRotateMode, string> = {
  local: 'Local',
  direction: 'Direction',
};
const REST_ROTATE_MODE_SHORT_LABEL: Record<RestRotateMode, string> = {
  local: 'L',
  direction: 'D',
};

function RestAxisValuesRow({
  label,
  labelIcon,
  compact,
  values,
  modeLabel,
  modeShortLabel,
  onToggleMode,
}: RestAxisValuesRowProps) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className={`${compact ? 'w-6' : 'w-16'} shrink-0`}>
        {compact ? (
          <Tooltip text={label} side="top" inModal>
            <span className="border-gray-30/80 dark:border-gray-60/80 bg-white/45 dark:bg-gray-90/45 text-primary inline-flex h-5 w-5 items-center justify-center rounded-md border">
              {labelIcon}
            </span>
          </Tooltip>
        ) : (
          <Text
            variant="studio/body/medium/xs"
            className={`${PANEL_WRITING_CLASSNAME} block truncate`}
          >
            {label}
          </Text>
        )}
      </div>
      <AxisTripletFields values={values} ariaLabelPrefix={label} />
      <Tooltip text={modeLabel} side="top" inModal>
        <button
          type="button"
          className="border-gray-30/80 dark:border-gray-60/80 bg-white/45 dark:bg-gray-90/45 hover:bg-white/65 dark:hover:bg-gray-90/70 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-md border px-1 transition-colors"
          onClick={onToggleMode}
        >
          <Text as="span" variant="studio/body/medium/xs" className="text-primary">
            {modeShortLabel}
          </Text>
        </button>
      </Tooltip>
    </div>
  );
}

export default function RestTransformViewer({
  bone,
  compactLayout,
}: RestTransformViewerProps) {
  const rowContentRef = useRef<HTMLDivElement>(null);
  const [detectedCompactLayout, setDetectedCompactLayout] = useState(false);
  const resolvedCompactLayout = compactLayout ?? detectedCompactLayout;
  const [translateMode, setTranslateMode] = useState<RestTranslateMode>('local');
  const [rotateMode, setRotateMode] = useState<RestRotateMode>('local');

  useLayoutEffect(() => {
    if (compactLayout !== undefined) return;
    const element = rowContentRef.current;
    if (!element) return;

    const updateCompactState = () => {
      setDetectedCompactLayout(
        element.getBoundingClientRect().width < COMPACT_LAYOUT_THRESHOLD_PX,
      );
    };
    updateCompactState();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => updateCompactState());
    observer.observe(element);
    return () => observer.disconnect();
  }, [compactLayout]);

  useEffect(() => {
    setTranslateMode('local');
    setRotateMode('local');
  }, [bone.index]);

  const restTranslate = useMemo(
    () => readBoneRestTranslation(bone, translateMode),
    [bone, translateMode],
  );
  const restRotate = useMemo(
    () => readBoneRestRotation(bone, rotateMode),
    [bone, rotateMode],
  );
  const restScale = useMemo(() => readBoneRestScale(bone), [bone]);
  const showRestScale = useMemo(() => hasNonIdentityScale(restScale), [restScale]);

  return (
    <Section title="Rest Pose" defaultOpen={false}>
      <div ref={rowContentRef} className="space-y-0.5">
        <RestAxisValuesRow
          label="Translate"
          labelIcon={<MoveIcon className="h-3 w-3" />}
          compact={resolvedCompactLayout}
          values={restTranslate}
          modeLabel={REST_TRANSLATE_MODE_LABEL[translateMode]}
          modeShortLabel={REST_TRANSLATE_MODE_SHORT_LABEL[translateMode]}
          onToggleMode={() =>
            setTranslateMode((mode) => (mode === 'local' ? 'world' : 'local'))
          }
        />
        <RestAxisValuesRow
          label="Rotate"
          labelIcon={<RotateIcon className="h-3 w-3" />}
          compact={resolvedCompactLayout}
          values={restRotate}
          modeLabel={REST_ROTATE_MODE_LABEL[rotateMode]}
          modeShortLabel={REST_ROTATE_MODE_SHORT_LABEL[rotateMode]}
          onToggleMode={() =>
            setRotateMode((mode) => (mode === 'local' ? 'direction' : 'local'))
          }
        />
        {showRestScale && (
          <TransformAxisValuesRow
            label="Scale"
            labelIcon={
              <Text
                as="span"
                variant="studio/body/medium/xs"
                className="text-primary"
              >
                S
              </Text>
            }
            compact={resolvedCompactLayout}
            values={restScale}
          />
        )}
      </div>
    </Section>
  );
}
